'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import PublicHeader from '@/components/PublicHeader';
import PublicFooter from '@/components/PublicFooter';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { createClient } from '@/lib/supabase/client';

function formatIDR(amount: number): string {
  if (amount >= 1000000000) return `Rp ${(amount / 1000000000).toFixed(1)} M`;
  if (amount >= 1000000) return `Rp ${(amount / 1000000).toFixed(0)} Jt`;
  return `Rp ${amount.toLocaleString('id-ID')}`;
}

interface Project {
  id: string;
  name: string;
  location: string;
  city: string;
  total_plots: number;
  available_plots?: number;
  price_from: number | null;
  price_to?: number | null;
  image_url: string | null;
  image_alt: string | null;
  legalitas: string | null;
  badge: string | null;
  status: string;
  description?: string | null;
  facilities?: string[] | null;
}

// Fallback: only ACTIVE projects shown
const fallbackProjects: Project[] = [];

export default function ProjectsPage() {
  const [projects, setProjects] = useState<Project[]>(fallbackProjects);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const supabase = createClient();

    const fetchProjects = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('projects')
        .select('id, name, location, city, total_plots, price_from, image_url, image_alt, legalitas, badge, status, description')
        .eq('status', 'ACTIVE')
        .order('created_at', { ascending: false });

      if (!error && data) {
        // Count available plots per project
        const withCounts = await Promise.all(
          data.map(async (proj) => {
            const { count } = await supabase
              .from('plots')
              .select('*', { count: 'exact', head: true })
              .eq('project_id', proj.id)
              .eq('status', 'AVAILABLE');
            return { ...proj, available_plots: count || 0 };
          })
        );
        setProjects(withCounts as Project[]);
      }
      setLoading(false);
    };

    fetchProjects();

    // Real-time: refetch when project status changes
    const channel = supabase
      .channel('public_projects_realtime')
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'projects' }, () => {
        fetchProjects();
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'projects' }, () => {
        fetchProjects();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-10">
        <div className="mb-8">
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
            <Link href="/homepage" className="hover:text-primary">Beranda</Link>
            <Icon name="ChevronRightIcon" size={14} />
            <span className="text-foreground font-medium">Project</span>
          </nav>
          <h1 className="text-3xl font-bold text-foreground mb-2">Semua Project BETALAND.ID</h1>
          <p className="text-muted-foreground">Pilih proyek yang sesuai dengan kebutuhan dan anggaran Anda</p>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-card rounded-2xl overflow-hidden border border-border animate-pulse">
                <div className="h-56 bg-muted" />
                <div className="p-5 space-y-3">
                  <div className="h-5 bg-muted rounded w-3/4" />
                  <div className="h-4 bg-muted rounded w-1/2" />
                  <div className="h-4 bg-muted rounded w-full" />
                  <div className="h-10 bg-muted rounded w-full mt-4" />
                </div>
              </div>
            ))}
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-24 text-muted-foreground">
            <Icon name="BuildingOfficeIcon" size={56} className="mx-auto mb-4 opacity-30" />
            <p className="text-xl font-semibold mb-2">Belum ada proyek aktif</p>
            <p className="text-sm">Proyek sedang dalam persiapan. Silakan cek kembali nanti.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((proj) =>
              <div key={proj.id} className="bg-card rounded-2xl overflow-hidden border border-border shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1 flex flex-col">
                <div className="relative h-56 overflow-hidden">
                  <AppImage
                    src={proj.image_url || 'https://img.rocket.new/generatedImages/rocket_gen_img_1ad9211d0-1784190870841.png'}
                    alt={proj.image_alt || proj.name}
                    width={500} height={224}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  {proj.badge && (
                    <span className="absolute top-3 left-3 px-2.5 py-1 bg-accent text-accent-foreground text-xs font-bold rounded-full">{proj.badge}</span>
                  )}
                  {proj.legalitas && (
                    <span className="absolute top-3 right-3 px-2.5 py-1 bg-white/90 text-primary text-xs font-bold rounded-full">{proj.legalitas}</span>
                  )}
                  <div className="absolute bottom-3 left-3 right-3">
                    <div className="flex items-center justify-between">
                      <span className="text-white font-bold text-lg drop-shadow">{proj.name}</span>
                      <span className="px-2 py-0.5 bg-primary/90 text-white text-xs font-bold rounded-full">{proj.available_plots ?? '—'} tersedia</span>
                    </div>
                  </div>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <p className="text-sm text-muted-foreground flex items-center gap-1 mb-3">
                    <Icon name="MapPinIcon" size={14} /> {proj.location}
                  </p>
                  {proj.description && (
                    <p className="text-sm text-muted-foreground leading-relaxed mb-4 line-clamp-2">{proj.description}</p>
                  )}
                  <div className="mt-auto pt-4 border-t border-border">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-xs text-muted-foreground">Harga mulai</p>
                        <p className="font-bold text-primary">{formatIDR(proj.price_from)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">{proj.total_plots} total kavling</p>
                        <p className="text-xs text-primary font-semibold">{proj.available_plots ?? '—'} tersedia</p>
                      </div>
                    </div>
                    <Link href={`/projects/${proj.id}`} className="block w-full text-center px-4 py-2.5 bg-primary text-primary-foreground font-semibold rounded-xl hover:bg-primary-dark transition-colors text-sm">
                      Lihat Detail Project
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <PublicFooter />
    </div>
  );
}